import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import KPICard from './components/KPICard';
import EngagementChart from './components/EngagementChart';
import PlatformComparison from './components/PlatformComparison';
import RealTimeFeed from './components/RealTimeFeed';
import FilterControls from './components/FilterControls';
import ContentPerformance from './components/ContentPerformance';
import HashtagAnalytics from './components/HashtagAnalytics';
import AlertsPanel from './components/AlertsPanel';

const SocialMediaAnalyticsDashboard = () => {
  const [filters, setFilters] = useState({
    dateRange: '7d',
    platform: 'all',
    metric: 'engagement'
  });
  const [isLoading, setIsLoading] = useState(false);

  // Mock KPI data
  const kpiData = [
    {
      title: 'Total Followers',
      value: '127.5K',
      change: '+12.5%',
      changeType: 'positive',
      icon: 'Users',
      color: 'primary'
    },
    {
      title: 'Engagement Rate',
      value: '8.7%',
      change: '+2.3%',
      changeType: 'positive',
      icon: 'Heart',
      color: 'success'
    },
    {
      title: 'Total Reach',
      value: '2.1M',
      change: '+18.9%',
      changeType: 'positive',
      icon: 'Eye',
      color: 'secondary'
    },
    {
      title: 'Conversions',
      value: '1,247',
      change: '-3.2%',
      changeType: 'negative',
      icon: 'Target',
      color: 'warning'
    }
  ];

  // Mock engagement trend data
  const engagementData = [
    { date: '2025-07-08', instagram: 4200, twitter: 2800, facebook: 1900 },
    { date: '2025-07-09', instagram: 4800, twitter: 3200, facebook: 2100 },
    { date: '2025-07-10', instagram: 5200, twitter: 2900, facebook: 2300 },
    { date: '2025-07-11', instagram: 4900, twitter: 3400, facebook: 2000 },
    { date: '2025-07-12', instagram: 5800, twitter: 3800, facebook: 2400 },
    { date: '2025-07-13', instagram: 6200, twitter: 4100, facebook: 2600 },
    { date: '2025-07-14', instagram: 6800, twitter: 4500, facebook: 2800 }
  ];

  // Mock platform comparison data
  const platformData = [
    { platform: 'Instagram', followers: 67500, engagement: 6800, reach: 1200000 },
    { platform: 'Twitter', followers: 45200, engagement: 4500, reach: 680000 },
    { platform: 'Facebook', followers: 14800, engagement: 2800, reach: 420000 }
  ];

  useEffect(() => {
    // Simulate data loading
    setIsLoading(true);
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, [filters]);

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleExport = (format, filterData) => {
    console.log(`Exporting data as ${format} with filters:`, filterData);
    // Implement export functionality
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="pt-16">
          <div className="max-w-7xl mx-auto px-4 py-8">
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div className="w-12 h-12 bg-primary rounded-lg flex items-center justify-center mx-auto mb-4 animate-pulse">
                  <div className="w-6 h-6 bg-primary-foreground rounded opacity-75"></div>
                </div>
                <div className="text-muted-foreground font-medium">Loading analytics...</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Social Media Analytics Dashboard - Dru Travels</title>
        <meta name="description" content="Comprehensive social media analytics dashboard for Dru Travels with real-time insights, engagement metrics, and performance tracking across all platforms." />
      </Helmet>

      <Header />
      
      <div className="pt-16">
        <div className="max-w-7xl mx-auto px-4 py-8">
          {/* Page Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-foreground mb-2">
              Social Media Analytics
            </h1>
            <p className="text-muted-foreground">
              Track your social media performance and engagement across all platforms
            </p>
          </div>

          {/* Filter Controls */}
          <FilterControls 
            onFiltersChange={handleFiltersChange}
            onExport={handleExport}
          />

          {/* KPI Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {kpiData.map((kpi, index) => (
              <KPICard
                key={index}
                title={kpi.title}
                value={kpi.value}
                change={kpi.change}
                changeType={kpi.changeType}
                icon={kpi.icon}
                color={kpi.color}
              />
            ))}
          </div>

          {/* Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            <EngagementChart 
              data={engagementData}
              title="Engagement Trends"
            />
            <PlatformComparison 
              data={platformData}
              title="Platform Performance"
            />
          </div>

          {/* Real-time Feed and Alerts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            <RealTimeFeed />
            <AlertsPanel />
          </div>

          {/* Content Performance */}
          <div className="mb-8">
            <ContentPerformance />
          </div>

          {/* Hashtag Analytics */}
          <div className="mb-8">
            <HashtagAnalytics />
          </div>
        </div>
      </div>
    </div>
  );
};

export default SocialMediaAnalyticsDashboard;